/**
 * Clinical roles that support compliance / HEDIS quality monitoring.
 * Student, social-worker, and general "other" schemas do NOT get this feature.
 */
const CLINICAL_ROLES = new Set(["doctor", "admin", "user", "medstudent"]);

/**
 * Returns true if the given role belongs to a clinical / medical schema
 * that should have access to the compliance monitoring features.
 *
 * @param {string|null} role  - The user's current role (from users table).
 * @returns {boolean}
 */
export function isComplianceSupported(role) {
  if (!role || typeof role !== "string") return false;
  // normalise: roles can be comma-separated, take the last segment (active role)
  const base = role.trim().includes(",")
    ? role.trim().split(",").pop().trim().toLowerCase()
    : role.trim().toLowerCase();
  return CLINICAL_ROLES.has(base);
}

export const DEFAULT_COMPLIANCE_SETTINGS = {
  enabled: true,
  medication_monitor_enabled: true,
  preventive_gaps_enabled: true,
  medication_refill_grace_days: 14,
  preventive_warning_days: 30,
  auto_document_chart: true,
  auto_create_outreach_task: true,
  scheduled_evaluate_enabled: true,
  scheduled_evaluate_hours: 24,
  patient_notifications_enabled: true,
  auto_notify_on_new_alert: true,
  scheduling_base_url: "",
  surescripts_webhook_secret: "",
  epic_care_gaps_enabled: true,
};

export function parseComplianceSettings(raw) {
  const base = { ...DEFAULT_COMPLIANCE_SETTINGS };
  if (!raw || typeof raw !== "object") return base;
  return {
    ...base,
    enabled: raw.enabled !== false,
    medication_monitor_enabled: raw.medication_monitor_enabled !== false,
    preventive_gaps_enabled: raw.preventive_gaps_enabled !== false,
    medication_refill_grace_days: Math.max(
      1,
      Math.min(90, Number(raw.medication_refill_grace_days) || 14),
    ),
    preventive_warning_days: Math.max(
      7,
      Math.min(180, Number(raw.preventive_warning_days) || 30),
    ),
    auto_document_chart: raw.auto_document_chart !== false,
    auto_create_outreach_task: raw.auto_create_outreach_task !== false,
    scheduled_evaluate_enabled: raw.scheduled_evaluate_enabled !== false,
    scheduled_evaluate_hours: Math.max(
      1,
      Math.min(168, Number(raw.scheduled_evaluate_hours) || 24),
    ),
    patient_notifications_enabled: raw.patient_notifications_enabled !== false,
    auto_notify_on_new_alert: raw.auto_notify_on_new_alert !== false,
    scheduling_base_url: String(raw.scheduling_base_url || "").trim(),
    surescripts_webhook_secret: String(raw.surescripts_webhook_secret || "").trim(),
    epic_care_gaps_enabled: raw.epic_care_gaps_enabled !== false,
  };
}
